import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {ConfirmationModalTitle} from '@/components/ui/confirmation-modal-atoms/ConfirmationModalAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {CreatePlannedAccessConfirmationSchema} from '@/features/planned-accesses/formSchemas'
import {useStorePlannedAccesses} from '@/features/planned-accesses/queries/useStorePlannedAccesses'
import {plannedAccessesValuesToPayload} from '@/features/planned-accesses/utils'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {resolveFm22WindowErrorLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'

export const CreatePlannedAccessesConfirmationDialog = ({
    dialogStore,
    onError,
    defaultValues
}: {
    dialogStore: DialogStore
    onError: () => void
    defaultValues: CreatePlannedAccessConfirmationSchema
}) => {
    const {t} = useTranslation()
    const form = useForm<CreatePlannedAccessConfirmationSchema>({
        resolver: zodResolver(CreatePlannedAccessConfirmationSchema),
        defaultValues
    })
    const projectId = useWatch({control: form.control, name: 'projectId'})
    const navigate = useNavigate()

    const {
        data: project,
        isLoading,
        isError
    } = useGetProject({
        urlParams: {projectId},
        params: {
            responseType: 'small'
        }
    })

    const {mutate: createPlannedAccesses, isPending} = useStorePlannedAccesses(projectId, {
        onSuccess: () => {
            navigate(`/projects/${projectId}/planned-accesses`, {replace: true})
        },
        onError: error => {
            console.error('Create planned accesses error: ', error)
            // FM22 window closed: the "check the form" banner would mislead, there is nothing to fix.
            const fm22WindowErrorLabel = resolveFm22WindowErrorLabel(error.response?.data?.code)
            if (fm22WindowErrorLabel) {
                toast.error(t(fm22WindowErrorLabel))
                dialogStore.setOpen(false)
                return
            }
            onError()
        }
    })

    const handleConfirmSubmit = form.handleSubmit(
        formValues => {
            createPlannedAccesses({
                additionalNotes: formValues.additionalNotes,
                targets: formValues.objectives,
                treatmentPlan: formValues.treatmentsPlan,
                plannedAccesses: plannedAccessesValuesToPayload(formValues, project?.sector),
                notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
                notificationDetails: formValues.notificationDetails
            })
        },
        error => {
            console.error('Validation create planned accesses error: ', error)
            onError()
        }
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <ConfirmationModalTitle>{t('planned_accesses:create_form_title')}</ConfirmationModalTitle>
                        {isLoading ? (
                            <Flexbox direction="column" gap={1.5}>
                                <Skeleton width={100} height={20} />
                                <Skeleton width={100} height={20} />
                            </Flexbox>
                        ) : isError ? (
                            <Text size="textSm" palette="danger" shade="500">
                                {t('errors:default')}
                            </Text>
                        ) : project?.patient.fullName ? (
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:patient_fullname_value', {fullName: project?.patient.fullName})}
                            </Text>
                        ) : (
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:no_results')}
                            </Text>
                        )}
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
